import { css } from '@emotion/react'
import { Cell, FormControl, Grid, HFlow, Icon, isEmpty, Tooltip, VFlow } from 'bold-ui'
import { ColorScale } from 'bold-ui/lib/styles/colors'
import { useAcessoLotacaoOrEstagio } from 'components/auth/useAcessoLotacao'
import { ErrorField, RichTextField } from 'components/form'
import {
  CboCuidadoCompartilhadoSelectField,
  CboCuidadoCompartilhadoSelectModel,
} from 'components/form/field/select/CboCuidadoCompartilhadoSelectField/CboCuidadoCompartilhadoSelectField'
import { CiapSelectField } from 'components/form/field/select/CiapSelectField/CiapSelectField'
import { CidSelectField } from 'components/form/field/select/CidSelectField/CidSelectField'
import { SimNaoRadioGroupField } from 'components/form/field/SimNaoRadioGroupField'
import theme from 'config/theme'
import { CiapCapituloEnum } from 'graphql/types.generated'
import { useAtendimentoContext } from 'hooks/atendimento-context/useAtendimentoContext'
import { Fragment, useEffect } from 'react'
import { useField } from 'react-final-form'
import { isUndefinedOrNull } from 'util/checks'
import { dateAsYyyyMmDd } from 'util/date/formatDate'
import { MetaRoot } from 'util/metaPath'

import { ClassificacaoPrioridadeField } from '../components/ClassificacaoPrioridadeField'
import { LotacoesCuidadoCompartilhadoSelectField } from '../components/LotacoesCuidadoCompartilhadoSelectField'
import { CuidadoCompartilhadoPlanoModel } from '../model-cuidadocompartilhado'
import { canRegisterCid10 } from './utils-cuidadoCompartilhado'

interface CuidadoCompartilhadoFormFieldsProps {
  path: MetaRoot<CuidadoCompartilhadoPlanoModel>
  isAtendimentoOdonto: boolean
  isEdit?: boolean
  disabled?: boolean
  richTextFieldRef?: React.RefObject<{ handleReset: () => void }>
  setDisabled?(isDisabled: boolean): void
}

export const CuidadoCompartilhadoFormFields = (props: CuidadoCompartilhadoFormFieldsProps) => {
  const { path, richTextFieldRef, disabled = false, isEdit = false, isAtendimentoOdonto = false, setDisabled } = props

  const {
    acesso: {
      cbo: { cbo2002 },
    },
  } = useAcessoLotacaoOrEstagio()

  const { cidadao, iniciadoEm } = useAtendimentoContext()

  const { acesso, profissional } = useAcessoLotacaoOrEstagio()
  const excludeProfissionaisIds = [profissional.id]
  const isIdentidadeGeneroEmpty = isEmpty(cidadao?.identidadeGeneroDbEnum)
  const cidadaoSexo = isIdentidadeGeneroEmpty ? cidadao?.sexo : null

  const {
    input: { value: cboValue },
  } = useField<CboCuidadoCompartilhadoSelectModel>(path.cbo.absolutePath(), { subscription: { value: true } })

  const {
    input: { value: ciapValue },
  } = useField<CboCuidadoCompartilhadoSelectModel>(path.problemasECondicoes.ciap.absolutePath(), {
    subscription: { value: true },
  })

  const cboId = cboValue?.cbo?.id
  const ciapId = ciapValue?.id
  const equipeId = acesso.equipe?.id
  useEffect(() => {
    setDisabled?.(isUndefinedOrNull(cboId))
  }, [cboId, setDisabled])

  const shouldRenderCID10Field = canRegisterCid10(cbo2002)

  return (
    <Grid>
      {(!disabled || isEdit) && (
        <Fragment>
          <Cell size={6}>
            <CboCuidadoCompartilhadoSelectField name={path.cbo} label='CBO' isAtendimentoOdonto={isAtendimentoOdonto} />
          </Cell>

          <Cell size={6}>
            <FormControl label='Profissional' required={!disabled}>
              <HFlow hSpacing={0.5} alignItems='baseline' style={styles.hFlow}>
                <LotacoesCuidadoCompartilhadoSelectField
                  name={path.lotacao}
                  unidadeSaudeId={acesso.unidadeSaude.id}
                  cboId={cboId}
                  excludeProfissionaisIds={excludeProfissionaisIds}
                  equipeSolicitanteId={equipeId}
                  isAtendimentoOdonto={isAtendimentoOdonto}
                  required={!disabled}
                  disabled={disabled}
                  date={dateAsYyyyMmDd(iniciadoEm)}
                />

                <Tooltip
                  text='O campo só traz profissionais de equipes NASF e eMulti vinculados a essa unidade.'
                  placement='top'
                >
                  <Icon icon='infoCircleFilled' fill='normal' size={1} style={styles.iconTooltip} />
                </Tooltip>
              </HFlow>
            </FormControl>
          </Cell>

          <Cell size={12}>
            <VFlow vSpacing={0.5}>
              <FormControl label='Problemas/condições' required>
                <Grid>
                  <Fragment>
                    <Cell size={6}>
                      <CiapSelectField
                        name={path.problemasECondicoes.ciap}
                        label='CIAP 2'
                        idadeCidadaoEmAnos={cidadao.idadeEmAnos}
                        sexo={cidadaoSexo}
                        capitulosExcluidos={[CiapCapituloEnum.PROCEDIMENTOS]}
                        excluirCIAPsDAB
                        menuMinWidth={620}
                        disabled={disabled}
                      />
                    </Cell>
                    {shouldRenderCID10Field && (
                      <Cell size={6}>
                        <CidSelectField
                          name={path.problemasECondicoes.cid10}
                          label='CID 10'
                          renderGrupoCiapRelacionada
                          idadeCidadaoEmAnos={cidadao.idadeEmAnos}
                          ciapRelacionada={ciapId}
                          sexo={cidadaoSexo}
                          disabled={disabled}
                        />
                      </Cell>
                    )}
                  </Fragment>
                </Grid>
              </FormControl>
              <ErrorField name={path.problemasECondicoes} ignoreObjectError />
            </VFlow>
          </Cell>

          <Cell size={12}>
            <ClassificacaoPrioridadeField name={path.prioridade} required={!disabled} />
          </Cell>

          <Cell size={12}>
            <RichTextField
              ref={richTextFieldRef}
              name={path.discussao}
              label='Discussão de caso clínico'
              placeholder='Insira objetivamente o relato do caso, além de dúvidas e questionamentos'
              maxLength={1000}
              required={!disabled}
            />
          </Cell>

          <Cell size={12}>
            <FormControl
              label='Após devida explicação, o cidadão declara concordar de forma livre e esclarecida que o agendamento da consulta com o especialista poderá ser mediado por tecnologia?'
              required={!disabled}
            >
              <HFlow>
                <SimNaoRadioGroupField name={path.cidadaoAceitaAtendTic} disabled={disabled} />
              </HFlow>
            </FormControl>
          </Cell>
        </Fragment>
      )}
    </Grid>
  )
}

const styles = {
  hFlow: css`
    grid-auto-columns: 1fr auto;
  `,
  iconTooltip: css`
    &:hover {
      fill: ${theme.pallete.primary.main};
    }
  `,
  icon: (color: ColorScale) => css`
    height: 1.5rem;
    width: 1.5rem;
    filter: drop-shadow(0 0.08rem 0.1rem ${color.c60});
  `,
  registrarHeading: (color: ColorScale) => css`
    color: ${color.c40};
  `,
}
